import { useId, useState } from "react";
import { connectorEncryptionCommand } from "../../../shared/credentials/connectorEncryptionExample";
import { connectorSealingAssetName } from "../../../shared/credentials/connectorSealingAsset";
import { PQ_KEY_PREFIX } from "../../../shared/credentials/hpkeFormat";
import { apiDocsPath } from "../../lib/apiPaths";
import { Box } from "../Box";
import { ButtonPress } from "../Button/ButtonPress";
import { CodeBlock } from "../CodeBlock";
import { Link } from "../Link";
import { Text } from "../Text";

export function ConnectorEncryptionGuide({
  publicKey,
  fingerprint,
}: {
  publicKey: string;
  fingerprint: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();

  return (
    <Box gap={12}>
      <ButtonPress
        variant="secondary"
        icon={expanded ? "chevron-down" : "chevron-right"}
        onPress={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        aria-controls={contentId}
        style={{ alignSelf: "flex-start" }}
      >
        How to encrypt connector secrets
      </ButtonPress>
      <div id={contentId} hidden={!expanded}>
        {expanded && (
          <ConnectorEncryptionInstructions
            publicKey={publicKey}
            fingerprint={fingerprint}
          />
        )}
      </div>
    </Box>
  );
}

function ConnectorEncryptionInstructions({
  publicKey,
  fingerprint,
}: {
  publicKey: string;
  fingerprint: string;
}) {
  const postQuantum = publicKey.startsWith(PQ_KEY_PREFIX);
  return (
    <Box gap={12}>
      <Text variant="text-small" color="var(--text-secondary)">
        {postQuantum ? (
          <>
            Download the key JSON above, then edit the Connector fields in this
            Node.js 22+ example. Run it locally and send the printed fields in a
            Connector POST or PATCH request.
          </>
        ) : (
          <>
            Replace <code>plaintextSecrets</code> in this Node.js 20+ example.
            Run it locally, then send the printed <code>secrets</code> in a
            Connector POST or PATCH request.
          </>
        )}
      </Text>
      <Text variant="text-small" color="var(--text-secondary)">
        {postQuantum
          ? "This example starts with a bearer API key. Set the final identifier, URL pattern, configuration, and secrets for your Connector before sealing. Add space and access fields for POST, or omit identifier for PATCH. To change a protected destination later, reseal affected secrets and PATCH them together within five minutes."
          : "This stack has no post-quantum key yet. Use this v1 example to encrypt API secrets; destination binding is unavailable until the key is activated."}
      </Text>
      <CodeBlock
        content={connectorEncryptionCommand(
          publicKey,
          fingerprint,
          postQuantum
            ? {
                identifier: "apiKey",
                urlPattern: "^https://api\\.example\\.com/",
              }
            : undefined,
          new URL(`/assets/${connectorSealingAssetName}`, window.location.href)
            .href
        )}
        language="javascript"
        wrap
      />
      <Text variant="text-small" color="var(--text-secondary)">
        Configuration fields stay as plain text.
      </Text>
      <Text variant="text-small">
        <Link
          to={apiDocsPath}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline" }}
        >
          View connector API documentation
        </Link>
      </Text>
    </Box>
  );
}
